import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { makeStyles } from "@mui/styles";
import dayjs from "dayjs";
import AxiosInstance from "../../services/networkServices";
import CommonPagination from "../../components/Table/CommonPagination";
import ExpandableText from "../../components/ExpandableText";
import Spinner from "../../components/Spinner";
import useDebounce from "../ManageUsers/useDebounce";
import ActionFilter from "../../components/ActionFilter";
import { showToast } from "../../store/slices/common";
import { useDispatch } from "react-redux";
import ConfirmDeleteModal from "../ManageBadge/ConfirmDeleteModal";
import PageLayout from "../../components/PageLayout";

const limit = 10;

const LOCATION_SOURCE_LABELS = {
  ip: "IP (approx.)",
  region: "profile region",
  sim: "SIM country",
  device: "device country",
};

/**
 * "City, US-CA" / "US-CA" / "US" from a location-shaped object, or "" when
 * nothing is set. Shared by the IP-derived and profile location fields.
 */
const formatLocation = (loc) => {
  if (!loc) return "";
  const region = loc.region || loc.regionCode || "";
  // ISO 3166-2 codes ("US-CA") already carry the country prefix.
  const country = region.startsWith(`${loc.country}-`) ? "" : loc.country;
  return [loc.city, region, country].filter(Boolean).join(", ");
};

const formatLocationSource = (loc) => {
  if (!loc || !loc.source) return "";
  const label = LOCATION_SOURCE_LABELS[loc.source] || loc.source;
  const accuracy = Number.isFinite(loc.accuracyKm) ? `, ~${loc.accuracyKm} km` : "";
  return `${label}${accuracy}`;
};

const useStyles = makeStyles((theme) => ({
  root: {
    height: "90vh",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  tableContainer: {
    flex: 1,
    overflowY: "auto",
    minHeight: "calc(100vh - 205px)",
    maxHeight: "calc(100vh - 205px)",
    boxShadow: "none !important",
  },
  table: {
    border: "1px solid rgba(224, 224, 224, 1)",
    minWidth: "100%",
    minHeight: "100%",
  },
  stickyHeader: {
    position: "sticky",
    top: "-1px",
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
  },
}));

export default function ManageFeedback() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [feedbacks, setFeedbacks] = useState([]);
  const [page, setPage] = useState(0);
  const [totalFeedbacks, setTotalFeedbacks] = useState(0);
  const [search, setSearch] = useState("");
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(-1);
  const debouncedSearch = useDebounce(search);

  useEffect(() => {
    fetchFeedbacks();
  }, [page, debouncedSearch]);

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      const response = await AxiosInstance.post("/feedback/list", {
        page: page + 1,
        limit: limit,
        search: debouncedSearch || undefined,
      });

      if (response.status === 200) {
        setFeedbacks(response.data.data.list);
        setTotalFeedbacks(response.data.data.resultCount);
      } else {
        console.error("Failed to fetch feedbacks:", response.data.message);
      }
    } catch (error) {
      console.error("Error fetching feedbacks:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleViewFeedback = (index) => {
    setSelectedItem(feedbacks[index]);
    setOpenViewDialog(true);
  };

  const handleResolveFeedback = async (index) => {
    try {
      setLoading(true);
      const feedback = feedbacks[index];
      const response = await AxiosInstance.put(`/feedback/${feedback._id}`, {
        isResolved: !feedback.isResolved,
      });

      if (response.status === 200) {
        fetchFeedbacks();
        dispatch(
          showToast({
            color: "success",
            msg: `Feedback marked as ${
              !feedback.isResolved ? "resolved" : "unresolved"
            }`,
          })
        );
      }
    } catch (error) {
      console.error("Error updating feedback:", error);
      dispatch(
        showToast({
          color: "error",
          msg: "Failed to update feedback status",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFeedback = (idx) => {
    setOpenDeleteModal(true);
    setDeleteIndex(idx);
  };

  const confirmDeleteFeedback = async () => {
    try {
      const deletedFeedback = feedbacks[deleteIndex];

      if (!deletedFeedback || !deletedFeedback._id) {
        throw new Error("Feedback ID is missing or invalid.");
      }

      const response = await AxiosInstance.delete(
        `/feedback/${deletedFeedback._id}`
      );

      if (response.status === 200) {
        await fetchFeedbacks();
        setOpenDeleteModal(false);
        dispatch(
          showToast({
            color: "success",
            msg: "Feedback deleted successfully.",
          })
        );
      } else {
        dispatch(
          showToast({ color: "error", msg: "Failed to delete feedback" })
        );
      }
    } catch (error) {
      dispatch(
        showToast({
          color: "error",
          msg: "Failed to delete feedback",
        })
      );
    }
  };

  return (
    <PageLayout
      title="Feedback"
      loading={loading}
      search={
        <input
          placeholder="Search username"
          type="text"
          onChange={handleSearchChange}
          className="search-field-ui"
        />
      }
    >
      <Box className={classes.root}>
        <Box
          sx={{
            padding: "15px 15px 0 15px",
            borderRadius: "6px",
            boxShadow:
              "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
          }}
        >
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow className={classes.stickyHeader}>
                  <TableCell
                    sx={{
                      fontSize: {
                        xs: "14px",
                        sm: "15px",
                        md: "15px",
                        lg: "16px",
                        xl: "16px",
                      },
                      color: "#000",
                      fontWeight: 600,
                      padding: {
                        xs: "10px 5px",
                        sm: "15px 10px",
                        md: "15px 10px",
                        lg: "20px 15px",
                        xl: "20px 15px",
                      },
                      backgroundColor: "#e0e0e0",
                    }}
                  >
                    Username
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: {
                        xs: "14px",
                        sm: "15px",
                        md: "15px",
                        lg: "16px",
                        xl: "16px",
                      },
                      color: "#000",
                      fontWeight: 600,
                      padding: {
                        xs: "10px 5px",
                        sm: "15px 10px",
                        md: "15px 10px",
                        lg: "20px 15px",
                        xl: "20px 15px",
                      },
                      backgroundColor: "#e0e0e0",
                    }}
                  >
                    Feedback
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: {
                        xs: "14px",
                        sm: "15px",
                        md: "15px",
                        lg: "16px",
                        xl: "16px",
                      },
                      color: "#000",
                      fontWeight: 600,
                      padding: {
                        xs: "10px 5px",
                        sm: "15px 10px",
                        md: "15px 10px",
                        lg: "20px 15px",
                        xl: "20px 15px",
                      },
                      backgroundColor: "#e0e0e0",
                    }}
                  >
                    Rating
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: {
                        xs: "14px",
                        sm: "15px",
                        md: "15px",
                        lg: "16px",
                        xl: "16px",
                      },
                      color: "#000",
                      fontWeight: 600,
                      padding: {
                        xs: "10px 5px",
                        sm: "15px 10px",
                        md: "15px 10px",
                        lg: "20px 15px",
                        xl: "20px 15px",
                      },
                      backgroundColor: "#e0e0e0",
                    }}
                  >
                    Location
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: {
                        xs: "14px",
                        sm: "15px",
                        md: "15px",
                        lg: "16px",
                        xl: "16px",
                      },
                      color: "#000",
                      fontWeight: 600,
                      padding: {
                        xs: "10px 5px",
                        sm: "15px 10px",
                        md: "15px 10px",
                        lg: "20px 15px",
                        xl: "20px 15px",
                      },
                      backgroundColor: "#e0e0e0",
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: {
                        xs: "14px",
                        sm: "15px",
                        md: "15px",
                        lg: "16px",
                        xl: "16px",
                      },
                      color: "#000",
                      fontWeight: 600,
                      padding: {
                        xs: "10px 5px",
                        sm: "15px 10px",
                        md: "15px 10px",
                        lg: "20px 15px",
                        xl: "20px 15px",
                      },
                      backgroundColor: "#e0e0e0",
                    }}
                  >
                    Date
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: {
                        xs: "14px",
                        sm: "15px",
                        md: "15px",
                        lg: "16px",
                        xl: "16px",
                      },
                      color: "#000",
                      fontWeight: 600,
                      padding: {
                        xs: "10px 5px",
                        sm: "15px 10px",
                        md: "15px 10px",
                        lg: "20px 15px",
                        xl: "20px 15px",
                      },
                      backgroundColor: "#e0e0e0",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {feedbacks.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{
                        textAlign: "center",
                        height: "calc(100vh - 280px)",
                        fontSize: {
                          xs: "12px",
                          sm: "13px",
                          md: "14px",
                          lg: "15px",
                          xl: "15px",
                        },
                        color: "#333",
                        padding: {
                          xs: "10px 5px",
                          sm: "10px",
                          md: "10px",
                          lg: "10px 15px",
                          xl: "10px 15px",
                        },
                      }}
                    >
                      No Feedbacks found
                    </TableCell>
                  </TableRow>
                ) : (
                  feedbacks.map((feedback, index) => (
                    <TableRow key={feedback._id}>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: "12px",
                            sm: "13px",
                            md: "14px",
                            lg: "15px",
                            xl: "15px",
                          },
                          color: "#333",
                          padding: {
                            xs: "10px 5px",
                            sm: "10px",
                            md: "10px",
                            lg: "10px 15px",
                            xl: "10px 15px",
                          },
                        }}
                      >
                        {feedback.username}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: "12px",
                            sm: "13px",
                            md: "14px",
                            lg: "15px",
                            xl: "15px",
                          },
                          color: "#333",
                          padding: {
                            xs: "10px 5px",
                            sm: "10px",
                            md: "10px",
                            lg: "10px 15px",
                            xl: "10px 15px",
                          },
                          maxWidth: "300px",
                        }}
                      >
                        <ExpandableText
                          text={feedback.feedback}
                          maxLength={40}
                          title="Feedback"
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: "12px",
                            sm: "13px",
                            md: "14px",
                            lg: "15px",
                            xl: "15px",
                          },
                          color: "#333",
                          padding: {
                            xs: "10px 5px",
                            sm: "10px",
                            md: "10px",
                            lg: "10px 15px",
                            xl: "10px 15px",
                          },
                        }}
                      >
                        {feedback.rating == null ? "n/a" : feedback.rating}/5
                      </TableCell>
                      <TableCell
                        title={formatLocationSource(feedback.location)}
                        sx={{
                          fontSize: {
                            xs: "12px",
                            sm: "13px",
                            md: "14px",
                            lg: "15px",
                            xl: "15px",
                          },
                          color: "#333",
                          padding: {
                            xs: "10px 5px",
                            sm: "10px",
                            md: "10px",
                            lg: "10px 15px",
                            xl: "10px 15px",
                          },
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatLocation(feedback.location) ? (
                          <>
                            {formatLocation(feedback.location)}
                            {formatLocationSource(feedback.location) && (
                              <span style={{ color: "#777" }}>
                                {" "}
                                ({formatLocationSource(feedback.location)})
                              </span>
                            )}
                          </>
                        ) : (
                          formatLocation(feedback.profileLocation) || "—"
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: "12px",
                            sm: "13px",
                            md: "14px",
                            lg: "15px",
                            xl: "15px",
                          },
                          color: "#333",
                          padding: {
                            xs: "10px 5px",
                            sm: "10px",
                            md: "10px",
                            lg: "10px 15px",
                            xl: "10px 15px",
                          },
                        }}
                      >
                        <span
                          style={{
                            color: feedback.isResolved ? "green" : "orange",
                            fontWeight: "bold",
                          }}
                        >
                          {feedback.isResolved ? "Resolved" : "Pending"}
                        </span>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: "12px",
                            sm: "13px",
                            md: "14px",
                            lg: "15px",
                            xl: "15px",
                          },
                          color: "#333",
                          padding: {
                            xs: "10px 5px",
                            sm: "10px",
                            md: "10px",
                            lg: "10px 15px",
                            xl: "10px 15px",
                          },
                        }}
                      >
                        {dayjs(feedback.createdAt).format("DD MMM YYYY")}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: "12px",
                            sm: "13px",
                            md: "14px",
                            lg: "15px",
                            xl: "15px",
                          },
                          color: "#333",
                          padding: {
                            xs: "10px 5px",
                            sm: "10px",
                            md: "10px",
                            lg: "10px 15px",
                            xl: "10px 15px",
                          },
                        }}
                      >
                        <ActionFilter
                          onView={() => handleViewFeedback(index)}
                          onEdit={() => handleResolveFeedback(index)}
                          onDelete={() => handleDeleteFeedback(index)}
                          index={index}
                          feedback={true}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CommonPagination
            rowsPerPage={limit}
            count={totalFeedbacks}
            page={page}
            onPageChange={handlePageChange}
          />
        </Box>
      </Box>

      {/* View Dialog for Feedback Details */}
      {openViewDialog && selectedItem && (
        <div className="modal" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog" style={{maxWidth: '600px', margin: '30px auto'}}>
            <div className="modal-content" style={{borderRadius: '8px', padding: '20px'}}>
              <div className="modal-header" style={{borderBottom: '1px solid #e0e0e0', paddingBottom: '15px'}}>
                <h5 style={{fontSize: '20px', fontWeight: 'bold', margin: 0}}>Feedback Details</h5>
                <button type="button" className="close" onClick={() => setOpenViewDialog(false)} style={{background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer'}}>
                  &times;
                </button>
              </div>
              <div className="modal-body" style={{padding: '15px 0'}}>
                <p><strong>Username:</strong> {selectedItem.username}</p>
                <p><strong>User ID:</strong> {selectedItem.userId}</p>
                <p><strong>Rating:</strong> {selectedItem.rating == null ? "n/a" : selectedItem.rating}/5</p>
                <p><strong>Status:</strong> <span style={{color: selectedItem.isResolved ? 'green' : 'orange'}}>{selectedItem.isResolved ? 'Resolved' : 'Pending'}</span></p>
                <p><strong>Date:</strong> {dayjs(selectedItem.createdAt).format("DD MMM YYYY - h:mm A")}</p>
                <p>
                  <strong>IP (approx.):</strong>{" "}
                  {formatLocation(selectedItem.location) || "unknown"}
                  {Number.isFinite(selectedItem.location?.accuracyKm) && (
                    <span style={{ color: "#777" }}>
                      {" "}
                      (~{selectedItem.location.accuracyKm} km)
                    </span>
                  )}
                  {selectedItem.location?.source &&
                    selectedItem.location.source !== "ip" && (
                      <span style={{ color: "#777" }}>
                        {" "}
                        ({formatLocationSource(selectedItem.location)})
                      </span>
                    )}
                </p>
                <p>
                  <strong>Profile location:</strong>{" "}
                  {formatLocation(selectedItem.profileLocation) || "not set"}
                </p>
                <p><strong>Feedback:</strong></p>
                <div style={{
                  padding: '15px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  marginTop: '10px',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {selectedItem.feedback}
                </div>
              </div>
              <div className="modal-footer" style={{borderTop: '1px solid #e0e0e0', paddingTop: '15px', display: 'flex', justifyContent: 'flex-end'}}>
                <button
                  style={{
                    backgroundColor: selectedItem.isResolved ? '#E57373' : '#4CAF50',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    marginRight: '10px',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    handleResolveFeedback(feedbacks.findIndex(f => f._id === selectedItem._id));
                    setOpenViewDialog(false);
                  }}
                >
                  Mark as {selectedItem.isResolved ? 'Unresolved' : 'Resolved'}
                </button>
                <button
                  style={{
                    backgroundColor: '#666',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  onClick={() => setOpenViewDialog(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {openDeleteModal && (
        <ConfirmDeleteModal
          feedback={true}
          onAccept={confirmDeleteFeedback}
          onReject={() => setOpenDeleteModal(false)}
        />
      )}
      <ToastContainer />
    </PageLayout>
  );
}
